import { useMemo, useState } from 'react';
import {
  Box, Button, Formblock, Select, SearchBar, Textfield,
  FormSection,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';
// services
import { format } from 'date-fns';
import { useGetAdminListQuery } from '../../../services/users/usersQueries';
import { useGetCustomerListQuery } from '../../../services/customers/customersQueries';
// utils
import { BOOKING_STATUS } from '../../../domain/bookings/bookingsUseCases';
import { getFullname } from '../../../domain/customers/customersUseCases';

/**
 * BookingFilters component.
 * @param {object} props
 * @param {URLSearchParams} props.searchParam
 * @param {Function} props.handleSearchParamChange
 * @param {string} props.statusFilter
 * @param {string} props.brokerFilter
 * @param {string} props.customerFilter
 * @returns {JSX.Element}
 */
function BookingFilters({
  searchParam,
  handleSearchParamChange,
  statusFilter,
  brokerFilter,
  customerFilter,
}) {
  const { t } = useTranslation();

  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });
  const [currentSearch, setCurrentSearch] = useState('');

  const {
    data: customers,
  } = useGetCustomerListQuery(
    {
      pageNumber: 1,
      pageSize: 10,
      search: currentSearch,
    },
    {
      keepPreviousData: false,
    },
  );

  const adminOptions = useMemo(() => admins?.data?.map(
    ({ firstname, lastname, id }) => ({ label: `${firstname} ${lastname}`, value: id }),
  ), [admins]);

  const customerOptions = useMemo(() => ([{
    label: t('layout.menu.entries.customers'),
    options: customers?.data?.map(({ firstname, lastname, id }) => ({
      label: `${firstname} ${lastname}`,
      value: id?.toString(),
    })) || [],
  }]), [customers?.data]);

  /**
   * Handle the customers search bar change.
   * @param {import('react').BaseSyntheticEvent} e
   */
  const handleSearchBarChange = (e) => {
    if (e.target.value === '') {
      handleSearchParamChange('customerId', '');
    }
    setCurrentSearch(e.target.value);
  };

  /**
   * Handle the click on the search item.
   * @param {string} id
   */
  const handleSearchItemClick = (id) => {
    const selectedCustomer = customers?.data.find(
      (customer) => customer.id?.toString() === id,
    );
    handleSearchParamChange('customerId', selectedCustomer?.id?.toString() || '');
  };

  const searchCustomerFullName = useMemo(() => {
    const fullNameFromParams = searchParam.get('customerfullname');
    if (fullNameFromParams) {
      return fullNameFromParams;
    }
    const customerParam = searchParam.get('customerId');
    const current = customers?.data.find(
      (customer) => customer?.id?.toString() === customerParam,
    );
    if (current) {
      const fullName = getFullname(current?.firstname, current?.lastname);
      handleSearchParamChange('customerfullname', fullName);
      return fullName;
    }
    return '';
  }, [customers?.data, searchParam]);

  return (
    <FormSection label={t('bookingList.filters.title')}>
      <div className="d-flex f-row f-wrap gap-s">
        {/* Filters */}
        <Formblock
          key="status"
          htmlFor="status"
          className="margin-bottom-xs"
        >
          <label htmlFor="status" className="p2-default">
            <span>{t('bookingList.filters.status')}</span>
            <Select
              // @ts-ignore
              id="status"
              name="status"
              className="background-neutral00"
              placeholder={t('bookingList.filters.placeholders.status')}
              options={BOOKING_STATUS}
              value={searchParam.get('status') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('status', e?.target?.value)}
            />
          </label>
        </Formblock>
        <Formblock
          key="broker"
          htmlFor="broker"
          className="margin-bottom-xs"
        >
          <label htmlFor="broker" className="p2-default">
            <span>{t('bookingList.filters.broker')}</span>
            <Select
              // @ts-ignore
              id="broker"
              name="broker"
              className="background-neutral00"
              placeholder={t('bookingList.filters.placeholders.broker')}
              options={adminOptions}
              value={searchParam.get('brokerId') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('brokerId', e?.target?.value)}
            />
          </label>
        </Formblock>
        <Formblock
          key="customer"
          htmlFor="customer"
          className="margin-bottom-xs"
        >
          <label htmlFor="customer" className="p2-default">
            <span>{t('bookingList.filters.customer')}</span>
            <SearchBar
              placeholder={t('bookingList.filters.placeholders.customer')}
              emptySearchResultLabel={t('customerList.search.noResult')}
              onChange={handleSearchBarChange}
              searchOptions={customerOptions}
              onItemClick={handleSearchItemClick}
              isSearchLoading={!customers}
              displayAvatar={false}
            />
          </label>
        </Formblock>
        <Formblock
          key="departureDateRangeStart"
          htmlFor="departureDateRangeStart"
          className="margin-bottom-xs"
        >
          <label htmlFor="departureDateRangeStart" className="p2-default">
            <span>{t('bookingList.filters.departureDateRangeStart')}</span>
            <Textfield
              // @ts-ignore
              id="departureDateRangeStart"
              name="departureDateRangeStart"
              type="date"
              value={searchParam.get('departureDateRangeStart') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('departureDateRangeStart', e.target.value)}
            />
          </label>
        </Formblock>
        <Formblock
          key="departureDateRangeEnd"
          htmlFor="departureDateRangeEnd"
          className="margin-bottom-xs"
        >
          <label htmlFor="departureDateRangeEnd" className="p2-default">
            <span>{t('bookingList.filters.departureDateRangeEnd')}</span>
            <Textfield
              // @ts-ignore
              id="departureDateRangeEnd"
              name="departureDateRangeEnd"
              type="date"
              value={searchParam.get('departureDateRangeEnd') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('departureDateRangeEnd', e.target.value)}
            />
          </label>
        </Formblock>
      </div>
      <Box direction="row" gap="s" className="margin-bottom-xs margin-top-s">
        {statusFilter && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('status', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.status')} : ${t(`bookingStatus.${statusFilter}`)}`}
          </Button>
        )}
        {brokerFilter && admins?.data && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('brokerId', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.broker')} : ${admins.data.find((admin) => admin.id.toString() === brokerFilter)?.firstname}`}
          </Button>
        )}
        {customerFilter && customers?.data && (
          <Button
            icon="cross"
            onClick={() => {
              handleSearchParamChange('customerId', '');
              handleSearchParamChange('customerfullname', '');
            }}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.customer')} : ${searchCustomerFullName}`}
          </Button>
        )}
        {searchParam.get('departureDateRangeStart') && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('departureDateRangeStart', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.departureDateRangeStart')} : ${format(new Date(searchParam.get('departureDateRangeStart')), 'dd/MM/yyyy')}`}
          </Button>
        )}
        {searchParam.get('departureDateRangeEnd') && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('departureDateRangeEnd', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.departureDateRangeEnd')} : ${format(new Date(searchParam.get('departureDateRangeEnd')), 'dd/MM/yyyy')}`}
          </Button>
        )}
        {searchParam.get('bookingIds') && (
          <Button
            icon="cross"
            onClick={() => {
              handleSearchParamChange('bookingIds', '');
              handleSearchParamChange('documentName', '');
            }}
            color="primary100"
            iconPosition="right"
          >
            {`${t('bookingList.filters.relatedDocument')} : ${searchParam.get('documentName')}`}
          </Button>
        )}
      </Box>
    </FormSection>
  );
}

export default BookingFilters;
